import { Box } from "@mui/material";
import React, { useState, useRef, useEffect, Children, useMemo } from "react";

type SplitLayoutProps = {
  children: React.ReactNode;
  initialSizes?: number[]; // percentages, e.g. [30, 70]
  minSizes?: number[]; // percentages, e.g. [10, 10]
  gutterWidth?: string; // e.g. "4px"
  gutterClassName?: string;
  gutterStyle?: React.CSSProperties;
  gutterBackground?: string;
  onSizesChange?: (sizesPct: number[]) => void; // percentages
  onDragStart?: (e: MouseEvent | React.MouseEvent) => void;
  onDragEnd?: () => void;
  showProperties?: boolean;
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

function SplitLayout({
  children,
  initialSizes: initialSizesProp = [],
  minSizes = [],
  gutterWidth = "4px",
  gutterClassName = "",
  gutterStyle = {},
  onSizesChange,
  showProperties,
  gutterBackground = "var(--surface-white)",
  className,
  onDragStart,
  onDragEnd,
  ...props
}: SplitLayoutProps) {
  const svgDataUri =
    "data:image/svg+xml;utf8,<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path fill-rule='evenodd' clip-rule='evenodd' d='M8.17391 2C8.70219 2 9.13044 2.42825 9.13044 2.95652L9.13043 20.1739C9.13043 20.7022 8.70219 21.1304 8.17391 21.1304C7.64564 21.1304 7.21739 20.7022 7.21739 20.1739L7.21739 2.95652C7.21739 2.42825 7.64564 2 8.17391 2Z' fill='%23192048'/><path fill-rule='evenodd' clip-rule='evenodd' d='M12 2C12.5283 2 12.9565 2.42825 12.9565 2.95652L12.9565 20.1739C12.9565 20.7022 12.5283 21.1304 12 21.1304C11.4717 21.1304 11.0435 20.7022 11.0435 20.1739L11.0435 2.95652C11.0435 2.42825 11.4717 2 12 2Z' fill='%23192048'/><path fill-rule='evenodd' clip-rule='evenodd' d='M15.8261 2C16.3544 2 16.7826 2.42825 16.7826 2.95652V20.1739C16.7826 20.7022 16.3544 21.1304 15.8261 21.1304C15.2978 21.1304 14.8696 20.7022 14.8696 20.1739L14.8696 2.95652C14.8696 2.42825 15.2978 2 15.8261 2Z' fill='%23192048'/><path d='M1 11.1808L5.30435 8.69565V13.6659L1 11.1808Z' fill='%23192048'/><path d='M23 11.1808L18.6957 8.69565V13.6659L23 11.1808Z' fill='%23192048'/></svg>";
  const cursorHotspotX = 12;
  const cursorHotspotY = 12;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerWidthRef = useRef(0); // px, only for drag math

  const [sizesPct, setSizesPct] = useState<number[]>([]); // store percentages
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const startXRef = useRef(0); // px
  const startSizesPctRef = useRef<number[]>([]); // percentages

  const childCount = Children.count(children);
  const initialSizes = useMemo(() => initialSizesProp, [initialSizesProp]);

  // init percentages
  useEffect(() => {
    if (childCount === 0) return;

    let next: number[];
    if (initialSizes.length === childCount) {
      next = initialSizes.slice();
    } else {
      const equal = 100 / childCount;
      next = Array(childCount).fill(equal);
    }

    // normalize to avoid drifting sums (e.g., 33.33 * 3 = 99.99)
    const sum = next.reduce((a, b) => a + b, 0);
    next = next.map((v, i) =>
      i === childCount - 1 ? Math.max(0, 100 - next.slice(0, -1).reduce((a, b) => a + b, 0)) : (v / sum) * 100,
    );

    setSizesPct(next);
  }, [childCount, initialSizes]);

  // track container width for drag conversion
  const updateContainerWidth = () => {
    const w = containerRef.current?.clientWidth || 0;
    containerWidthRef.current = w;
  };

  useEffect(() => {
    updateContainerWidth();
    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth);
  }, []);

  function onMouseMove(e: MouseEvent) {
    if (dragIndex === null) return;
    const cw = containerWidthRef.current;
    if (!cw) return;

    const dxPx = e.clientX - startXRef.current;
    const dxPct = (dxPx / cw) * 100;

    const newSizes = [...startSizesPctRef.current];
    const leftIndex = dragIndex;
    const rightIndex = dragIndex + 1;

    const total = startSizesPctRef.current[leftIndex] + startSizesPctRef.current[rightIndex];

    let newLeft = startSizesPctRef.current[leftIndex] + dxPct;
    let newRight = startSizesPctRef.current[rightIndex] - dxPct;

    const minLeft = minSizes[leftIndex] ?? 0;
    const minRight = minSizes[rightIndex] ?? 0;

    // clamp
    if (newLeft < minLeft) {
      newLeft = minLeft;
      newRight = total - newLeft;
    }
    if (newRight < minRight) {
      newRight = minRight;
      newLeft = total - newRight;
    }
    if (newLeft < minLeft || newRight < minRight) return;

    // snap to 2 decimals to reduce float drift
    newSizes[leftIndex] = Math.max(0, +newLeft.toFixed(2));
    newSizes[rightIndex] = Math.max(0, +newRight.toFixed(2));

    setSizesPct(newSizes);
    onSizesChange?.(newSizes);
  }

  function onMouseUp() {
    if (dragIndex !== null) {
      onDragEnd?.();
    }
    setDragIndex(null);
    document.body.style.userSelect = "";
  }

  // drag lifecycle
  useEffect(() => {
    if (dragIndex !== null) {
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
    };
  }, [dragIndex]);

  function handleMouseDown(index: number, e: React.MouseEvent) {
    if (!showProperties) return;
    e.preventDefault();
    setDragIndex(index);
    startXRef.current = e.clientX;
    startSizesPctRef.current = [...sizesPct];
    onDragStart?.(e);
  }

  const renderableSizes =
    sizesPct.length === childCount ? sizesPct : Array(childCount).fill(100 / Math.max(1, childCount));

  return (
    <div ref={containerRef} className={`flex w-full h-full overflow-hidden max-w-full ${className}`} {...props}>
      {Children.map(children, (child: any, i: any) => (
        <React.Fragment key={`pane-${child?.id}`}>
          <div
            className={`flex-none h-full relative`}
            style={{
              width: `${Math.max(0, renderableSizes[i] || 0)}%`,
            }}
          >
            {i % 2 !== 0 && (
              <Box
                className={`h-full group absolute top-0 start-0 z-10 ${gutterClassName}`}
                onMouseDown={(e: React.MouseEvent<Element, MouseEvent>) => handleMouseDown(Math.floor(i / 2), e)}
                style={{
                  ...(showProperties && {
                    cursor: `url("${svgDataUri}") ${cursorHotspotX} ${cursorHotspotY}, ew-resize`,
                    background: gutterBackground,
                  }),
                }}
              >
                <div
                  key={`gutter-${child?.id}`}
                  className={`flex-none h-full transition-all duration-200 ${
                    dragIndex !== null ? "bg-[var(--selection)]" : "group-hover:bg-[var(--selection)]"
                  } `}
                  style={{
                    userSelect: "none",
                    width: gutterWidth,
                    ...gutterStyle,
                  }}
                />
              </Box>
            )}
            {child}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export default SplitLayout;
